/**
 * Avatar Editor Modal — character sprite picker + special sprites.
 * Tabs: Character / Special / Saved.
 * Paid items are permanent VibeCoin unlocks (backend routes/outfits.py).
 */
import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { X, User, Save as SaveIcon, Lock, Check, Scissors, Glasses, Shirt, Sparkles, Coins } from 'lucide-react';
import AvatarPreview, { drawTurtle, drawSpecialSprite } from './AvatarPreview';
import { drawOutfitLayers, drawMannequin, drawDiscoHead } from './outfitLayers';
import {
  OUTFIT_COLORS, HAIR_ITEMS, FACE_ITEMS, HAT_ITEMS, SHIRT_ITEMS, PANTS_ITEMS,
  TURTLE_MAN, SPECIAL_ITEMS, CATEGORY_FIELD, rarityOf, RARITY_META,
} from './outfitItems';

const RarityBadge = ({ price, big }) => {
  const tier = rarityOf(price);
  const meta = RARITY_META[tier];
  return (
    <span
      className={`${big ? 'text-[10px] px-2 py-0.5' : 'text-[8px] px-1.5 py-px'} rounded-full font-bold uppercase tracking-wide`}
      style={{ color: meta.color, backgroundColor: meta.bg, border: `1px solid ${meta.color}55` }}
      data-testid={`rarity-${tier}`}
    >
      {meta.label}
    </span>
  );
};

const CHARACTERS = [
  { id: 'base_m', name: 'Base (M)', thumb: '/sprites/thumbs/player_base_m.png' },
  { id: 'base_f', name: 'Base (F)', thumb: '/sprites/thumbs/player_base_f.png' },
  { id: 'male', name: 'Suit', thumb: '/sprites/thumbs/player_sheet.png' },
  { id: 'mage', name: 'Mage', thumb: '/sprites/thumbs/player_mage.png' },
  { id: 'athlete', name: 'Athlete', thumb: '/sprites/thumbs/player_athlete.png' },
  { id: 'punk', name: 'Punk', thumb: '/sprites/thumbs/player_punk.png' },
  { id: 'royalty', name: 'Royalty', thumb: '/sprites/thumbs/player_royalty.png' },
  { id: 'formal', name: 'Formal', thumb: '/sprites/thumbs/player_formal.png' },
  { id: 'dj', name: 'DJ', thumb: '/sprites/thumbs/player_dj.png' },
  { id: 'vampire', name: 'Vampire', thumb: '/sprites/thumbs/player_vampire.png' },
  { id: 'ninja', name: 'Ninja', thumb: '/sprites/thumbs/player_ninja.png' },
  { id: 'pilot', name: 'Pilot', thumb: '/sprites/thumbs/player_pilot.png' },
  { id: 'cowboy', name: 'Cowboy', thumb: '/sprites/thumbs/player_cowboy.png' },
  { id: 'surfer', name: 'Surfer', thumb: '/sprites/thumbs/player_surfer.png' },
  { id: 'pirate', name: 'Pirate', thumb: '/sprites/thumbs/player_pirate.png' },
  { id: 'astronaut', name: 'Astronaut', thumb: '/sprites/thumbs/player_astronaut.png' },
  { id: 'knight', name: 'Knight', thumb: '/sprites/thumbs/player_knight.png' },
  { id: 'chef', name: 'Chef', thumb: '/sprites/thumbs/player_chef.png' },
  { id: 'zombie', name: 'Zombie', thumb: '/sprites/thumbs/player_zombie.png' },
  { id: 'alien', name: 'Alien', thumb: '/sprites/thumbs/player_alien.png' },
  { id: 'samurai', name: 'Samurai', thumb: '/sprites/thumbs/player_samurai.png' },
  { id: 'clown', name: 'Clown', thumb: '/sprites/thumbs/player_clown.png' },
  { id: 'devil', name: 'Devil', thumb: '/sprites/thumbs/player_devil.png' },
  { id: 'angel', name: 'Angel', thumb: '/sprites/thumbs/player_angel.png' },
  { id: 'skeleton', name: 'Skeleton', thumb: '/sprites/thumbs/player_skeleton.png' },
  { id: 'werewolf', name: 'Werewolf', thumb: '/sprites/thumbs/player_werewolf.png' },
  { id: 'boxer', name: 'Boxer', thumb: '/sprites/thumbs/player_boxer.png' },
  { id: 'firefighter', name: 'Firefighter', thumb: '/sprites/thumbs/player_firefighter.png' },
  { id: 'doctor', name: 'Doctor', thumb: '/sprites/thumbs/player_doctor.png' },
  { id: 'wizard', name: 'Wizard', thumb: '/sprites/thumbs/player_wizard.png' },
  { id: 'ballerina', name: 'Ballerina', thumb: '/sprites/thumbs/player_ballerina.png' },
];

// Swatch grid (skin tones + item colors)
const ColorGrid = ({ items, selected, onSelect, testIdPrefix, small }) => (
  <div className="flex gap-2 flex-wrap">
    {items.map((item) => (
      <button
        key={item.id}
        onClick={() => onSelect(item)}
        className={`${small ? 'w-8 h-8' : 'w-12 h-12 sm:w-14 sm:h-14'} rounded-full border-2 transition-all ${
          selected === (item.hex || item.id)
            ? 'border-white ring-2 ring-purple-500 scale-110'
            : 'border-white/30 hover:scale-105 hover:border-white/60'
        }`}
        style={{
          backgroundColor: item.color || item.hex,
          boxShadow: selected === (item.hex || item.id) ? '0 0 12px rgba(168,85,247,0.5)' : 'none',
        }}
        title={item.name || item.id}
        data-testid={`${testIdPrefix}-${item.id}`}
      />
    ))}
  </div>
);

// Mini canvas preview: mannequin + single outfit layer
const ItemThumb = ({ category, itemId, colors }) => {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, c.width, c.height);
    drawMannequin(ctx, 32, 68, 1);
    if (itemId !== 'none') {
      const fake = {
        [CATEGORY_FIELD[category]]: itemId,
        hairColor: colors?.hairColor,
        shirtColor: colors?.shirtColor,
        pantsColor: colors?.pantsColor,
      };
      drawOutfitLayers(ctx, fake, 32, 68, 'down', false, 0, 1);
    }
  }, [category, itemId, colors]);
  return (
    <canvas ref={ref} width={64} height={78} className="w-12 h-14 sm:w-14 sm:h-16" style={{ imageRendering: 'pixelated' }} />
  );
};

// Turtle Man preview canvas
const TurtleManThumb = () => {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, c.width, c.height);
      drawTurtle(ctx, 60, 60, 1.1, 'down', true, Date.now() / 220);
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, []);
  return <canvas ref={ref} width={120} height={120} style={{ imageRendering: 'pixelated' }} />;
};

// Disco Head preview canvas
const DiscoHeadThumb = () => {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, c.width, c.height);
      drawMannequin(ctx, 60, 108, 1.6);
      drawDiscoHead(ctx, 60, 108 + (21 - 58) * 1.6, 1.6);
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, []);
  return <canvas ref={ref} width={120} height={120} style={{ imageRendering: 'pixelated' }} />;
};

// Generic walking sheet-special preview canvas
const SheetSpecialThumb = ({ specialId }) => {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, c.width, c.height);
      drawSpecialSprite(ctx, specialId, 60, 60, 1.1, 'down', true, Date.now() / 220);
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, [specialId]);
  return <canvas ref={ref} width={120} height={120} style={{ imageRendering: 'pixelated' }} />;
};

const SPECIAL_THUMBS = {
  special_turtle_man: TurtleManThumb,
  special_disco_head: DiscoHeadThumb,
  special_dj_shark: () => <SheetSpecialThumb specialId="special_dj_shark" />,
  special_party_gorilla: () => <SheetSpecialThumb specialId="special_party_gorilla" />,
  special_disco_yeti: () => <SheetSpecialThumb specialId="special_disco_yeti" />,
};

// Generic animated thumb: mannequin + any special/effect via drawOutfitLayers
const EffectThumb = ({ specialId }) => {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, c.width, c.height);
      drawMannequin(ctx, 60, 108, 1.6);
      drawOutfitLayers(ctx, { special: specialId }, 60, 108, 'down', false, 0, 1.6);
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, [specialId]);
  return <canvas ref={ref} width={120} height={120} style={{ imageRendering: 'pixelated' }} />;
};

// Grid of shop items for one category
const ItemGrid = ({ items, category, avatar, ownedItems, onEquip, onBuy, pendingBuy, colors }) => {
  const field = CATEGORY_FIELD[category];
  const current = avatar[field] || 'none';
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
      {items.map((item) => {
        const owned = item.price === 0 || ownedItems.includes(item.id);
        const equipped = current === item.id;
        const isPending = pendingBuy === item.id;
        return (
          <button
            key={item.id}
            onClick={() => (owned ? onEquip(field, item.id) : onBuy(item, field))}
            className={`relative p-2 rounded-lg border-2 transition-all flex flex-col items-center ${
              equipped
                ? 'border-purple-500 ring-2 ring-purple-500/50 bg-purple-500/10'
                : isPending
                  ? 'border-amber-400 bg-amber-500/10'
                  : 'border-white/15 hover:border-white/40 hover:bg-white/5'
            }`}
            data-testid={`outfit-item-${item.id === 'none' ? `${category}-none` : item.id}`}
            title={item.name}
          >
            {equipped && (
              <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-purple-500 flex items-center justify-center">
                <Check className="w-3 h-3 text-white" />
              </span>
            )}
            <ItemThumb category={category} itemId={item.id} colors={colors} />
            <span className="text-[11px] text-white/85 mt-0.5 leading-tight text-center">{item.name}</span>
            {item.id !== 'none' && <RarityBadge price={item.price} />}
            {item.price > 0 && !owned ? (
              <span className={`text-[10px] flex items-center gap-0.5 mt-0.5 ${isPending ? 'text-amber-300 font-semibold' : 'text-yellow-400'}`}>
                {isPending ? 'Tap to confirm' : (<><Lock className="w-2.5 h-2.5" /> {item.price}</>)}
              </span>
            ) : item.price > 0 ? (
              <span className="text-[10px] text-emerald-400 mt-0.5">Owned</span>
            ) : (
              <span className="text-[10px] text-white/35 mt-0.5">Free</span>
            )}
          </button>
        );
      })}
    </div>
  );
};

const AvatarEditorModal = ({
  isOpen,
  onClose,
  avatar,
  updateAvatar,
  isMobile,
  vibecoins,
  savedOutfits,
  saveCurrentOutfit,
  loadOutfit,
  deleteOutfit,
  isTurtle,
  SKIN_TONES,
  ownedItems = [],
  onPurchaseItem,
}) => {
  const [activeTab, setActiveTab] = useState('body');
  const [pendingBuy, setPendingBuy] = useState(null);

  if (!isOpen) return null;

  const selectedCharName =
    CHARACTERS.find((c) => c.id === (avatar.gender || 'male'))?.name || 'Suit';

  const itemColors = {
    hairColor: avatar.hairColor,
    shirtColor: avatar.shirtColor,
    pantsColor: avatar.pantsColor,
  };
  const handleEquip = (field, itemId) => {
    updateAvatar(field, itemId === (avatar[field] || 'none') && itemId !== 'none' ? 'none' : itemId);
    setPendingBuy(null);
  };

  const handleBuy = (item, field) => {
    if (pendingBuy === item.id) {
      setPendingBuy(null);
      onPurchaseItem?.(item, field);
    } else {
      setPendingBuy(item.id);
      setTimeout(() => setPendingBuy((p) => (p === item.id ? null : p)), 4000);
    }
  };

  const gridProps = {
    avatar,
    ownedItems,
    onEquip: handleEquip,
    onBuy: handleBuy,
    pendingBuy,
    colors: itemColors,
  };

  return (
    <div
      className="fixed inset-0 z-[9990] flex items-center justify-center bg-black/80 backdrop-blur-sm p-2 sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="avatar-editor-overlay"
    >
      <Card
        className={`w-full ${
          isMobile ? 'max-w-full h-[95vh]' : 'max-w-3xl h-[90vh]'
        } bg-gray-900 border-purple-500/30 flex flex-col overflow-hidden`}
        data-testid="avatar-editor-modal"
      >
        {/* Sticky Header */}
        <CardHeader className="flex flex-row items-center justify-between border-b border-white/10 flex-shrink-0 py-3">
          <CardTitle className="text-white flex items-center gap-2 text-base sm:text-lg">
            <User className="w-5 h-5 text-purple-400" /> Avatar Editor
          </CardTitle>
          <div className="flex items-center gap-3">
            <span className="text-xs text-yellow-400 font-medium flex items-center gap-1" data-testid="editor-vibecoins">
              <Coins className="w-3.5 h-3.5" /> {vibecoins ?? 0}
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              data-testid="close-avatar-editor"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>

        {/* Sticky Live Preview */}
        <div className="flex-shrink-0 border-b border-white/10 bg-gradient-to-b from-black/40 to-transparent flex items-center justify-center py-2">
          <AvatarPreview avatar={avatar} isTurtle={isTurtle} />
        </div>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="flex-1 flex flex-col overflow-hidden"
        >
          <TabsList className="grid grid-cols-3 mx-3 mt-2 mb-1 flex-shrink-0 bg-black/40">
            <TabsTrigger value="body" data-testid="tab-body" className="text-xs">
              <User className="w-3.5 h-3.5 sm:mr-1" />
              <span className="hidden sm:inline">Character</span>
            </TabsTrigger>
            <TabsTrigger value="special" data-testid="tab-special" className="text-xs">
              <Sparkles className="w-3.5 h-3.5 sm:mr-1" />
              <span className="hidden sm:inline">Special</span>
            </TabsTrigger>
            <TabsTrigger value="saved" data-testid="tab-saved" className="text-xs">
              <SaveIcon className="w-3.5 h-3.5 sm:mr-1" />
              <span className="hidden sm:inline">Saved</span>
            </TabsTrigger>
          </TabsList>

          <CardContent className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-4">
            {/* CHARACTER TAB */}
            <TabsContent value="body" className="space-y-5 mt-0">
              <div>
                <Label className="text-white mb-2 block text-sm">
                  Character{' '}
                  <span className="text-xs text-white/50">
                    ({CHARACTERS.length} options — in-club appearance)
                  </span>
                </Label>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {CHARACTERS.map((ch) => {
                    const selected = (avatar.gender || 'male') === ch.id;
                    return (
                      <button
                        key={ch.id}
                        onClick={() => updateAvatar('gender', ch.id)}
                        className={`p-2 rounded-lg border-2 transition-all flex flex-col items-center ${
                          selected
                            ? 'border-purple-500 ring-2 ring-purple-500/50 bg-purple-500/10'
                            : 'border-white/15 hover:border-white/40 hover:bg-white/5'
                        }`}
                        data-testid={`character-${ch.id}`}
                        title={ch.name}
                      >
                        <img
                          src={ch.thumb}
                          alt={ch.name}
                          className="w-16 h-16 sm:w-20 sm:h-20 object-contain"
                          style={{ imageRendering: 'pixelated' }}
                        />
                        <span className="text-[11px] text-white/85 mt-1">{ch.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label className="text-white mb-2 block text-sm">
                  Skin Tone{' '}
                  <span className="text-xs text-white/40">
                    ({SKIN_TONES.length} options — recolors your character's actual skin)
                  </span>
                </Label>
                <ColorGrid
                  items={SKIN_TONES}
                  selected={avatar.skinTone}
                  onSelect={(item) => updateAvatar('skinTone', item.id)}
                  testIdPrefix="skin"
                />
              </div>

              <div data-testid="sprite-hue-section">
                <Label className="text-white mb-2 block text-sm">
                  Sprite Tint
                  <span className="text-xs text-white/50 ml-2">
                    (recolors your in-club sprite)
                  </span>
                </Label>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-md border border-white/30 flex-shrink-0"
                      style={{
                        background: `hsl(${avatar.spriteHue || 0}, 70%, 50%)`,
                        boxShadow: `0 0 12px hsla(${avatar.spriteHue || 0}, 80%, 50%, 0.6)`,
                      }}
                    />
                    <div className="flex-1">
                      <span className="text-[11px] text-white/60 block mb-1">Color</span>
                      <Slider
                        data-testid="sprite-hue-slider"
                        value={[avatar.spriteHue || 0]}
                        onValueChange={(v) => updateAvatar('spriteHue', v[0])}
                        min={0}
                        max={359}
                        step={1}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[11px] text-white/60 w-16 flex-shrink-0">Vibrance</span>
                    <Slider
                      data-testid="sprite-sat-slider"
                      value={[avatar.spriteSat ?? 100]}
                      onValueChange={(v) => updateAvatar('spriteSat', v[0])}
                      min={0}
                      max={200}
                      step={5}
                      className="flex-1"
                    />
                    <span className="text-[11px] text-white/50 w-10 text-right">{avatar.spriteSat ?? 100}%</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[11px] text-white/60 w-16 flex-shrink-0">Brightness</span>
                    <Slider
                      data-testid="sprite-bright-slider"
                      value={[avatar.spriteBright ?? 100]}
                      onValueChange={(v) => updateAvatar('spriteBright', v[0])}
                      min={60}
                      max={150}
                      step={5}
                      className="flex-1"
                    />
                    <span className="text-[11px] text-white/50 w-10 text-right">{avatar.spriteBright ?? 100}%</span>
                  </div>
                  <button
                    data-testid="sprite-hue-reset"
                    onClick={() => {
                      updateAvatar('spriteHue', 0);
                      updateAvatar('spriteSat', 100);
                      updateAvatar('spriteBright', 100);
                    }}
                    className="text-xs px-3 py-1.5 rounded bg-white/10 text-white/70 hover:bg-white/20"
                  >
                    Reset tint
                  </button>
                </div>
              </div>
            </TabsContent>

            {/* SPECIAL TAB */}
            <TabsContent value="special" className="mt-0 space-y-4">
              {SPECIAL_ITEMS.map((sp) => {
                const owned = ownedItems.includes(sp.id);
                const equipped = avatar.special === sp.id;
                const Thumb = SPECIAL_THUMBS[sp.id] || (() => <EffectThumb specialId={sp.id} />);
                return (
                  <div
                    key={sp.id}
                    className={`rounded-xl border-2 p-4 flex flex-col sm:flex-row items-center gap-4 ${
                      equipped
                        ? 'border-emerald-400 bg-emerald-500/10'
                        : 'border-emerald-500/40 bg-gradient-to-br from-emerald-900/30 to-teal-900/20'
                    }`}
                    data-testid={`${sp.slug}-card`}
                  >
                    <div className="flex-shrink-0 rounded-lg bg-black/30 p-2">
                      {Thumb && <Thumb />}
                    </div>
                    <div className="flex-1 text-center sm:text-left">
                      <h3 className="text-emerald-300 font-bold text-lg flex items-center justify-center sm:justify-start gap-2">
                        <Sparkles className="w-4 h-4" /> {sp.name} <RarityBadge price={sp.price} big />
                      </h3>
                      <p className="text-white/60 text-sm mt-1">{sp.description}</p>
                      <div className="mt-3">
                        {!owned ? (
                          <Button
                            onClick={() => handleBuy(sp, 'special')}
                            className={`${
                              pendingBuy === sp.id
                                ? 'bg-amber-500 hover:bg-amber-400'
                                : 'bg-emerald-600 hover:bg-emerald-500'
                            }`}
                            data-testid={`buy-${sp.slug}`}
                          >
                            {pendingBuy === sp.id ? (
                              'Tap again to confirm'
                            ) : (
                              <span className="flex items-center gap-1.5">
                                <Lock className="w-3.5 h-3.5" /> Unlock — {sp.price} <Coins className="w-3.5 h-3.5" />
                              </span>
                            )}
                          </Button>
                        ) : equipped ? (
                          <Button
                            onClick={() => updateAvatar('special', null)}
                            variant="outline"
                            className="border-emerald-400 text-emerald-300"
                            data-testid={`unequip-${sp.slug}`}
                          >
                            Take it off
                          </Button>
                        ) : (
                          <Button
                            onClick={() => updateAvatar('special', sp.id)}
                            className="bg-emerald-600 hover:bg-emerald-500"
                            data-testid={`equip-${sp.slug}`}
                          >
                            Equip {sp.name}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <p className="text-white/35 text-xs text-center">
                More special outfits coming soon…
              </p>
            </TabsContent>

            {/* SAVED TAB */}
            <TabsContent value="saved" className="space-y-4 mt-0">
              <div className="flex items-center justify-between">
                <Label className="text-white text-sm">
                  Saved Looks ({savedOutfits.length}/10)
                </Label>
                <Button
                  onClick={saveCurrentOutfit}
                  size="sm"
                  className="bg-green-600 hover:bg-green-500 text-xs"
                  disabled={savedOutfits.length >= 10}
                  data-testid="save-outfit-btn"
                >
                  <SaveIcon className="w-3 h-3 mr-1" /> Save Current
                </Button>
              </div>

              {savedOutfits.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {savedOutfits.map((outfit) => {
                    const ch = CHARACTERS.find(
                      (c) => c.id === (outfit.avatar?.gender || 'male'),
                    );
                    return (
                      <div
                        key={outfit.id}
                        className="relative bg-black/40 rounded-lg p-3 border border-white/10 hover:border-purple-500/50 transition-colors"
                        data-testid={`saved-outfit-${outfit.id}`}
                      >
                        <button onClick={() => loadOutfit(outfit)} className="w-full text-left">
                          <div className="flex items-center gap-2 mb-1">
                            {ch && (
                              <img
                                src={ch.thumb}
                                alt={ch.name}
                                className="w-8 h-8 object-contain"
                                style={{ imageRendering: 'pixelated' }}
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="text-xs text-white font-medium truncate">
                                {outfit.name}
                              </div>
                              <div className="text-[10px] text-white/50 truncate">
                                {outfit.avatar?.special === TURTLE_MAN.id ? 'Turtle Man' : ch?.name || 'Suit'}
                              </div>
                            </div>
                          </div>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteOutfit(outfit.id);
                          }}
                          className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500/80 hover:bg-red-500 flex items-center justify-center"
                          data-testid={`delete-outfit-${outfit.id}`}
                        >
                          <X className="w-3 h-3 text-white" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-white/40 text-sm text-center py-8">
                  No saved looks yet. Pick a character and tap{' '}
                  <em>Save Current</em> here. Currently wearing:{' '}
                  <span className="text-white/70">{selectedCharName}</span>
                </p>
              )}
            </TabsContent>
          </CardContent>
        </Tabs>

        {/* Sticky Footer */}
        <div className="flex-shrink-0 border-t border-white/10 p-3 bg-gray-900">
          <Button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:opacity-90"
            data-testid="done-avatar-btn"
          >
            Done
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default AvatarEditorModal;
